/**
 * Canonical platform permission strings, copied verbatim from
 * openapi/generated/route_policy.gen.go. Controls are gated on the exact
 * permission the route demands, so no rendered button can 403.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { useSession } from "@/lib/session/use-session";

export const PLATFORM_PERMISSIONS = {
  approvalRead: "platform.approval.read",
  approvalDecide: "platform.approval.decide",
  approvalDelegate: "platform.approval.delegate",
  notificationRead: "platform.notification.read",
  notificationManage: "platform.notification.manage",
  branchRead: "platform.branch.read",
  branchWrite: "platform.branch.write",
  userRead: "platform.user.read",
  auditRead: "platform.audit.read",
} as const;

export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[keyof typeof PLATFORM_PERMISSIONS];

/** Direct-route authorization: denied pages never mount or issue queries. */
export function PlatformPermissionBoundary({
  permission,
  children,
}: {
  permission: PlatformPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="platform-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="platform-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <h1 id="platform-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("platform.permissions.title")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
